import { PRIVILEGE_SLUGS_BY_ID } from '@ubeya/shared/hooks/generic/usePrivileges';
import { ACCOUNT_BILLING_STATUS_MAP, ACCOUNT_STATUSES } from '@ubeya/shared/constants';
import privileges from '@ubeya/shared-web/privileges';
import { SORT_BY_NUMBER } from '@ubeya/shared/utils/sorting';

const INTERCOM_APP_ID = 'ty9i6fky';
const INTERCOM_SOURCE_WEB = 'web';
const INTERCOM_CONTACT_TYPE_ADMIN = 'admin';

const ADMIN_TYPE_LABELS = {
  [privileges.BRANCH_ADMIN]: 'Branch manager',
  [privileges.ACCOUNT_ADMIN]: 'Account manager'
};

// ACCOUNT_STATUSES minus Demo, whose value is undefined and would match any account with no billing status.
const ACCOUNT_TYPE_LABELS = {
  ...Object.fromEntries(ACCOUNT_STATUSES.filter(({ value }) => value).map(({ value, label }) => [value, label])),
  [ACCOUNT_BILLING_STATUS_MAP.STAFFING_AGENCY_FREEMIUM]: 'Supplier Freemium'
};

const EXTRA_MODULE_SLUGS = ['payroll', 'directory', 'compliance', 'invoice', 'app_store', 'help_desk'];

// The BI tool is an account level flag rather than a module, but it is reported alongside them.
const BI_TOOL_MODULE = 'bi_tool';

const MAX_ATTRIBUTE_LENGTH = 255;

// Fixed slots per list: empties clear a shrunk list, the cap bounds new attributes, six fits all 38 privileges.
const ATTRIBUTE_CHUNKS = 6;

const SEPARATOR = ', ';

const ELLIPSIS = '...';

// Intercom drops array attributes and caps strings at 255, so a list becomes chunks split between whole entries.
const packEntries = (entries) => {
  const chunks = [];

  entries.forEach((entry) => {
    const lastIndex = chunks.length - 1;

    if (lastIndex < 0) {
      chunks.push(entry.slice(0, MAX_ATTRIBUTE_LENGTH));
      return;
    }

    const merged = `${chunks[lastIndex]}${SEPARATOR}${entry}`;

    if (merged.length > MAX_ATTRIBUTE_LENGTH) {
      chunks.push(entry.slice(0, MAX_ATTRIBUTE_LENGTH));
    } else {
      chunks[lastIndex] = merged;
    }
  });

  return chunks;
};

// An unknown list is omitted so Intercom keeps its column; an empty one sends the base key blank to clear it.
const splitAttribute = (key, entries) => {
  if (!entries) {
    return {};
  }

  const chunks = packEntries(entries);

  // More than the slots can carry: mark the last one so support can see the list is cut short.
  if (chunks.length > ATTRIBUTE_CHUNKS) {
    const lastSlot = ATTRIBUTE_CHUNKS - 1;

    chunks[lastSlot] = `${chunks[lastSlot].slice(0, MAX_ATTRIBUTE_LENGTH - ELLIPSIS.length)}${ELLIPSIS}`;
  }

  return Object.fromEntries(
    Array.from({ length: ATTRIBUTE_CHUNKS }, (_, index) => [
      index === 0 ? key : `${key}_${index + 1}`,
      chunks[index] || ''
    ])
  );
};

const getAdminType = (privilegeIds = []) => {
  // Branch manager wins when an admin holds both, matching AdminsList and the admins export.
  if (privilegeIds.includes(privileges.BRANCH_ADMIN)) {
    return ADMIN_TYPE_LABELS[privileges.BRANCH_ADMIN];
  }

  if (privilegeIds.includes(privileges.ACCOUNT_ADMIN)) {
    return ADMIN_TYPE_LABELS[privileges.ACCOUNT_ADMIN];
  }

  return '';
};

const getExtraModules = ({ modules, enableBi }) => {
  const activeModules = EXTRA_MODULE_SLUGS.filter((slug) => !!modules?.[slug]);

  return (enableBi ? [...activeModules, BI_TOOL_MODULE] : activeModules).join(SEPARATOR);
};

// Anything but an array counts as unknown and omits the column; entries without an id are dropped.
const splitNamedIds = (key, entities) =>
  splitAttribute(
    key,
    Array.isArray(entities) &&
      entities
        .filter((entity) => entity?.id !== undefined && entity?.id !== null)
        .sort(SORT_BY_NUMBER('id'))
        .map(({ id, name }) => (name ? `${name}(${id})` : `${id}`))
  );

// Raw slugs, not usePrivileges().mappedPrivileges, which t() would translate into the admin's own language.
const splitPrivileges = (privilegeIds) =>
  splitNamedIds(
    'privileges',
    Array.isArray(privilegeIds) && privilegeIds.map((id) => ({ id, name: PRIVILEGE_SLUGS_BY_ID[id] || 'privilege' }))
  );

export const buildIntercomBootPayload = ({
  user,
  account,
  billingStatus,
  accounts,
  branches,
  adminBranches,
  modules,
  privilegeIds
} = {}) => {
  const { id, firstName, lastName, email, phone, intercomHash } = user || {};
  const { id: accountId, name, enableBi } = account || {};

  return {
    app_id: INTERCOM_APP_ID,
    name: [firstName, lastName].filter(Boolean).join(' '),
    email,
    ...(phone && { phone }),
    user_id: id,
    user_hash: intercomHash,
    admin_id: id,
    ...(Array.isArray(privilegeIds) && { admin_type: getAdminType(privilegeIds) }),
    type: INTERCOM_CONTACT_TYPE_ADMIN,
    ...splitNamedIds('accounts', accounts),
    ...splitNamedIds('account_branches', branches),
    ...splitNamedIds('admin_branches', adminBranches),
    ...(billingStatus && { account_type: ACCOUNT_TYPE_LABELS[billingStatus] || billingStatus }),
    ...(modules && { extra_modules: getExtraModules({ modules, enableBi }) }),
    ...splitPrivileges(privilegeIds),
    source: INTERCOM_SOURCE_WEB,
    // Unix seconds, not milliseconds: Intercom types a date attribute on first write and never retypes it.
    web_synced_at: Math.floor(Date.now() / 1000),
    company: {
      id: accountId,
      name: name || ''
    }
  };
};
